// Seed laden + afgeleide indexen bouwen.
// De seed is gegenereerd en alleen-lezen; we mogen indexen vrijelijk eromheen bouwen.

import { getSeed } from './seeddb.js'

export async function loadRegions() {
  const res = await fetch('/data/regions.json', { cache: 'no-cache' })
  if (!res.ok) throw new Error(`regions.json laden mislukt: ${res.status}`)
  return res.json()
}

export async function loadSeed(file) {
  try {
    const local = await getSeed(file)
    if (local) {
      const indexes = buildIndexes(local)
      return { data: local, indexes, isLocal: true }
    }
  } catch (e) {
    console.warn('IndexedDB lezen mislukt, fallback naar server:', e)
  }
  const res = await fetch(`/data/${file}`, { cache: 'no-cache' })
  if (!res.ok) throw new Error(`seed laden mislukt: ${res.status}`)
  const data = await res.json()
  const indexes = buildIndexes(data)
  return { data, indexes, isLocal: false }
}

function buildIndexes(data) {
  const nodesById = new Map()
  for (const n of data.nodes ?? []) nodesById.set(n.id, n)

  const peopleById = new Map()
  for (const p of data.people ?? []) peopleById.set(p.id, p)

  const compendiumById = new Map()
  for (const c of data.compendium ?? []) compendiumById.set(c.id, c)

  const hikesById = new Map()
  for (const h of data.hikes ?? []) hikesById.set(h.id, h)

  // parentId → [child-nodes]
  const childrenOf = new Map()
  for (const n of data.nodes ?? []) {
    const k = n.parentId ?? null
    if (!childrenOf.has(k)) childrenOf.set(k, [])
    childrenOf.get(k).push(n)
  }

  // nodeId → [person] (reverse-index op people[].places[].nodeId)
  const peopleByNode = new Map()
  for (const person of data.people ?? []) {
    for (const place of person.places ?? []) {
      if (!peopleByNode.has(place.nodeId)) peopleByNode.set(place.nodeId, [])
      peopleByNode.get(place.nodeId).push({ person, relation: place.relation })
    }
  }

  // nodeId → [{ guide, note }]; één node mag in meerdere guides staan
  const guidesByNode = new Map()
  for (const g of data.guides ?? []) {
    for (const e of g.entries ?? []) {
      if (!guidesByNode.has(e.nodeId)) guidesByNode.set(e.nodeId, [])
      guidesByNode.get(e.nodeId).push({ guide: g, note: e.note })
    }
  }

  // nodeId → [compendiumItem] (zowel via node.compendiumIds als compendium.nodeIds)
  const compendiumByNode = new Map()
  function add(nodeId, item) {
    if (!compendiumByNode.has(nodeId)) compendiumByNode.set(nodeId, [])
    const arr = compendiumByNode.get(nodeId)
    if (!arr.includes(item)) arr.push(item)
  }
  for (const n of data.nodes ?? []) {
    for (const cid of n.compendiumIds ?? []) {
      const item = compendiumById.get(cid)
      if (item) add(n.id, item)
    }
  }
  for (const c of data.compendium ?? []) {
    for (const nid of c.nodeIds ?? []) add(nid, c)
  }

  // walkTour: kinderen van een node met walkTour, gesorteerd op order
  function walkTourFor(parentId) {
    const kids = childrenOf.get(parentId) ?? []
    const tour = kids
      .filter(k => k.walkTour && Number.isInteger(k.walkTour.order))
      .sort((a, b) => a.walkTour.order - b.walkTour.order)
    return tour
  }

  // Meta-lookups
  const categoryById = new Map((data.meta?.categories ?? []).map(c => [c.id, c]))
  const eraById = new Map((data.meta?.eras ?? []).map(e => [e.id, e]))
  const tierById = new Map((data.meta?.tiers ?? []).map(t => [t.id, t]))

  return {
    nodesById, peopleById, compendiumById, hikesById,
    childrenOf, peopleByNode, guidesByNode, compendiumByNode,
    categoryById, eraById, tierById,
    walkTourFor,
  }
}

// Helper: filter nodes door de actieve filterset.
// favorites is een Object { nodeId: true }; alleen relevant als filters.onlyFavorites
// aanstaat.
export function filterNodes(data, indexes, filters, favorites = null) {
  return (data.nodes ?? []).filter(n => {
    if (filters.onlyFavorites) {
      if (!favorites || !favorites[n.id]) return false
    }
    if (filters.tiers && n.visit && !filters.tiers.has(n.visit)) return false
    if (filters.stars && (n.stars ?? 0) < filters.stars) return false
    if (filters.categories && !filters.categories.has(n.category)) return false
    if (filters.eras) {
      const eras = n.eras ?? []
      if (!eras.some(e => filters.eras.has(e))) return false
    }
    return true
  })
}

// Helper: bepaal of een node een stub is (geen sections, geen gegenereerde toevoeging)
export function isStub(node, generatedSections) {
  if (node.sections && node.sections.length > 0) return false
  if (generatedSections && generatedSections[node.id]) return false
  return true
}

// Mergen: server-gegenereerde sections worden achter de curated sections geplakt
export function effectiveSections(node, generatedSections) {
  const curated = node.sections ?? []
  const generated = (generatedSections && generatedSections[node.id]) ?? []
  return [...curated, ...generated]
}
